import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { barkodSesiniHazirla, basariliBarkodSesiCal } from '../barkodSesi';
import { BARKOD_KAMERA_KISITLARI, barkodOkuyucuOlustur, kameraOdaklamasiniIyilestir } from '../barkodKamera';
import {
  Archive,
  Beef,
  Boxes,
  Box,
  CalendarDays,
  Camera,
  ChevronDown,
  ChevronUp,
  Cookie,
  CupSoda,
  FileText,
  Leaf,
  MoreVertical,
  Package,
  PackageCheck,
  PackageOpen,
  ScanBarcode,
  Sandwich,
  ShoppingBasket,
  Wheat,
  X,
} from 'lucide-react';

const KATEGORILER = [
  { key: '', label: 'Tümü', Icon: FileText },
  { key: 'icecek', label: 'İçecek', Icon: CupSoda },
  { key: 'kuru_gida', label: 'Kuru Gıda', Icon: Wheat },
  { key: 'et', label: 'Et', Icon: Beef },
  { key: 'sos', label: 'Sos', Icon: ShoppingBasket },
  { key: 'ambalaj', label: 'Ambalaj', Icon: Archive },
  { key: 'manav', label: 'Manav', Icon: Leaf },
  { key: 'ekmek', label: 'Ekmek', Icon: Sandwich },
  { key: 'tatli', label: 'Tatlı', Icon: Cookie },
  { key: 'diger', label: 'Diğer', Icon: Box },
];

const AYLAR = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function tarihFmt(str) {
  if (!str) return '';
  const dotted = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotted) {
    return str;
  }
  const d = new Date(str + 'T00:00:00');
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
}

function sayi(value) {
  return Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}


function saatFmt(h) {
  if (h?.saat) return h.saat;
  const match = String(h?.olusturma || '').match(/(\d{2}:\d{2})/);
  return match ? match[1] : '';
}

function aciklamaFmt(h) {
  return h?.islemi_yapan || 'Eski kayıt';
}

function fiyatFmt(value) {
  return `₺${Number(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function kategoriBilgi(key) {
  return KATEGORILER.find(k => k.key === key) || KATEGORILER[0];
}

function barkodlaUrunBul(kod, urunler) {
  const temizKod = String(kod || '').trim().toLocaleLowerCase('tr-TR');
  if (!temizKod) return null;
  return urunler.find(u => String(u.urun_id || '').trim().toLocaleLowerCase('tr-TR') === temizKod) || null;
}

export default function GelenGiden({ secilenSube, yenile, onHareket, ay, yil, donemAcik, onKilitAc, onNotify, onConfirm }) {
  const now = new Date();
  const ayKapaliHam = yil < now.getFullYear() || (yil === now.getFullYear() && ay < now.getMonth() + 1);
  const ayKapali = ayKapaliHam && !donemAcik;

  const [kategori, setKategori] = useState('');
  const [urunler, setUrunler] = useState([]);
  const [hareketler, setHareketler] = useState([]);
  const [ciro, setCiro] = useState(0);
  const [seciliUrunId, setSeciliUrunId] = useState(null);
  const [form, setForm] = useState({
    urun_id: '', hareket_turu: 'giris', miktar: '', birim_fiyat: '', aciklama: '',
    tarih: now.toISOString().split('T')[0]
  });
  const [yukleniyor, setYukleniyor] = useState(false);
  const [harModal, setHarModal] = useState(false);
  const [duzenlenenHareket, setDuzenlenenHareket] = useState(null);
  const [acikMenu, setAcikMenu] = useState(null);
  const [acikGun, setAcikGun] = useState(null);
  const [arama, setArama] = useState('');
  const [kameraAcik, setKameraAcik] = useState(false);
  const videoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scanningRef = useRef(false);
  const bekleyenBarkodRef = useRef(null);

  useEffect(() => {
    const getir = async () => {
      setYukleniyor(true);
      try {
        const params = { ay, yil };
        if (kategori) params.kategori = kategori;
        if (secilenSube) params.sube_id = secilenSube;

        const [stokData, hareketData] = await Promise.all([
          api.getStokOzet(params),
          api.getHareketler(params)
        ]);

        setUrunler(stokData);
        setHareketler(hareketData);
        setSeciliUrunId(id => (id && stokData.some(u => u.id === id) ? id : null));
      } catch (e) {
        console.error(e);
      }
      setYukleniyor(false);
    };
    getir();
  }, [ay, yil, kategori, secilenSube, yenile]);

  useEffect(() => {
    let cancelled = false;
    const ciroGetir = async () => {
      try {
        const params = { ay, yil };
        if (secilenSube) params.sube_id = secilenSube;
        const data = await api.getCiro(params);
        if (!cancelled) setCiro(Number(data?.ciro || 0));
      } catch (_) {
        if (!cancelled) setCiro(0);
      }
    };
    ciroGetir();
    return () => { cancelled = true; };
  }, [ay, yil, secilenSube, yenile]);

  useEffect(() => () => kamerayiKapat(), []);

  // Kategori filtresi kaldırıldıktan sonra okutulan barkodu yeni listede arar.
  useEffect(() => {
    const kod = bekleyenBarkodRef.current;
    if (!kod || yukleniyor) return;
    bekleyenBarkodRef.current = null;
    const bulunan = barkodlaUrunBul(kod, urunler);
    if (bulunan) {
      setSeciliUrunId(bulunan.id);
      onNotify?.('success', `${bulunan.ad} bulundu.`);
    } else {
      onNotify?.('error', 'Bu barkod/Ürün ID ile kayıtlı ürün bulunamadı.');
    }
  }, [urunler, yukleniyor]);

  const seciliUrun = useMemo(
    () => urunler.find(u => u.id === seciliUrunId) || null,
    [urunler, seciliUrunId]
  );

  const seciliHareketler = useMemo(() => {
    if (!seciliUrun) return [];
    return hareketler.filter(h => h.urun_id === seciliUrun.id);
  }, [hareketler, seciliUrun]);

  const filtreliUrunler = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase('tr-TR');
    if (!q) return urunler;
    return urunler.filter(u => (
      String(u.ad || '').toLocaleLowerCase('tr-TR').includes(q) ||
      String(u.urun_id || '').toLocaleLowerCase('tr-TR').includes(q)
    ));
  }, [urunler, arama]);


  const gunlukHareketler = useMemo(() => {
    const gruplar = new Map();
    seciliHareketler.forEach(h => {
      const key = h.tarih_iso || h.tarih;
      if (!gruplar.has(key)) {
        gruplar.set(key, { key, tarih: h.tarih, tarih_iso: h.tarih_iso, giris: 0, cikis: 0, girisDeger: 0, cikisDeger: 0, kayitlar: [] });
      }
      const grup = gruplar.get(key);
      const deger = Number(h.hareket_degeri ?? (Number(h.miktar || 0) * Number(h.birim_fiyat || 0)));
      if (h.hareket_turu === 'giris') {
        grup.giris += Number(h.miktar || 0);
        grup.girisDeger += deger;
      }
      if (h.hareket_turu === 'cikis') {
        grup.cikis += Number(h.miktar || 0);
        grup.cikisDeger += deger;
      }
      grup.kayitlar.push(h);
    });
    return [...gruplar.values()]
      .map(g => ({ ...g, kayitlar: [...g.kayitlar].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)) }))
      .sort((a, b) => String(b.tarih_iso || b.tarih).localeCompare(String(a.tarih_iso || a.tarih)));
  }, [seciliHareketler]);

  const kategoriAktif = kategoriBilgi(kategori);
  const KategoriIcon = kategoriAktif.Icon;
  const seciliFiyat = Number(seciliUrun?.fiyat || 0);
  const devredenDegeri = Number(seciliUrun?.devreden_stok || 0) * seciliFiyat;
  const gelenDegeri = Number(seciliUrun?.gelen_deger ?? (Number(seciliUrun?.gelen || 0) * seciliFiyat));
  const kullanilanDeger = Number(seciliUrun?.kullanilan_deger ?? (Number(seciliUrun?.giden || 0) * seciliFiyat));
  const guncelDeger = Number(seciliUrun?.toplam_deger ?? (Number(seciliUrun?.guncel_stok || 0) * seciliFiyat));
  const urunKullanimYuzdesi = ciro > 0 ? Math.round((kullanilanDeger / ciro) * 100) : null;
  const urunKullanimRengi = urunKullanimYuzdesi === null ? '#94a3b8'
    : urunKullanimYuzdesi <= 10 ? '#059669'
    : urunKullanimYuzdesi <= 20 ? '#d97706'
    : '#dc2626';
  const para = (value) => `₺${Number(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const kamerayiKapat = () => {
    scanningRef.current = false;
    if (scannerControlsRef.current) {
      scannerControlsRef.current.stop();
      scannerControlsRef.current = null;
    }
    setKameraAcik(false);
  };

  const kameraBaslat = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onNotify?.('error', 'Bu tarayıcı kamera erişimini desteklemiyor.');
      return;
    }
    try {
      barkodSesiniHazirla();
      setKameraAcik(true);
      window.setTimeout(() => barkodTara(), 0);
    } catch (e) {
      onNotify?.('error', 'Kamera açılamadı. Tarayıcı kamera iznini kontrol edin.');
    }
  };

  const barkodTara = async () => {
    if (!videoRef.current) return;

    scanningRef.current = true;
    try {
      const reader = barkodOkuyucuOlustur();
      scannerControlsRef.current = await reader.decodeFromConstraints(
        BARKOD_KAMERA_KISITLARI,
        videoRef.current,
        (result) => {
          if (!result || !scanningRef.current) return;
          const kod = String(result.getText() || '').trim();
          basariliBarkodSesiCal();
          kamerayiKapat();
          setArama(kod);
          const bulunan = barkodlaUrunBul(kod, urunler);
          if (bulunan) {
            setSeciliUrunId(bulunan.id);
            onNotify?.('success', `${bulunan.ad} bulundu.`);
          } else if (kategori) {
            // Ürün başka bir kategoride olabilir; filtreyi kaldırıp yeniden arıyoruz.
            bekleyenBarkodRef.current = kod;
            setSeciliUrunId(null);
            setKategori('');
          } else {
            setSeciliUrunId(null);
            onNotify?.('error', 'Bu barkod/Ürün ID ile kayıtlı ürün bulunamadı.');
          }
        }
      );
      await kameraOdaklamasiniIyilestir(videoRef.current);
    } catch (e) {
      setKameraAcik(false);
      onNotify?.('error', 'Kamera ile barkod okunamadı. Kamera iznini kontrol edin.');
    }
  };

  const modalAc = (urun = null, hareketTuru = 'giris') => {
    setDuzenlenenHareket(null);
    setForm(f => ({
      ...f,
      urun_id: urun ? String(urun.id) : '',
      hareket_turu: hareketTuru,
      miktar: '',
      birim_fiyat: hareketTuru === 'giris' && urun ? String(urun.fiyat ?? '') : '',
      aciklama: ''
    }));
    setHarModal(true);
  };

  const duzenleModalAc = (hareket) => {
    setDuzenlenenHareket(hareket);
    setAcikMenu(null);
    setForm({
      urun_id: String(hareket.urun_id),
      hareket_turu: hareket.hareket_turu,
      miktar: String(hareket.miktar),
      birim_fiyat: hareket.hareket_turu === 'giris' ? String(hareket.birim_fiyat ?? '') : '',
      aciklama: hareket.aciklama || '',
      tarih: hareket.tarih_iso || hareket.tarih
    });
    setHarModal(true);
  };

  const kaydet = async (e) => {
    e.preventDefault();
    if (!form.urun_id || !form.miktar || (form.hareket_turu === 'giris' && form.birim_fiyat === '')) {
      onNotify?.('error', form.hareket_turu === 'giris' ? 'Ürün, miktar ve fiyat zorunludur.' : 'Ürün ve miktar zorunludur.');
      return;
    }
    try {
      const payload = { ...form, urun_id: parseInt(form.urun_id), miktar: sayiyaCevir(form.miktar) };
      if (form.hareket_turu === 'giris') {
        payload.birim_fiyat = sayiyaCevir(form.birim_fiyat);
      } else {
        delete payload.birim_fiyat;
      }
      if (duzenlenenHareket) {
        await api.updateHareket(duzenlenenHareket.id, payload);
      } else {
        await api.createHareket(payload);
      }
      onNotify?.('success', `${form.hareket_turu === 'giris' ? 'Giriş' : 'Çıkış'} ${duzenlenenHareket ? 'güncellendi' : 'kaydedildi'}.`);
      setForm(f => ({ ...f, urun_id: '', miktar: '', birim_fiyat: '', aciklama: '' }));
      setDuzenlenenHareket(null);
      setHarModal(false);
      onHareket();
    } catch (err) {
      onNotify?.('error', err.message);
    }
  };

  const sayiyaCevir = (deger) => Number(String(deger ?? '').replace(',', '.'));

  const silHareket = async (id) => {
    try {
      await api.deleteHareket(id);
      setAcikMenu(null);
      onHareket();
      onNotify?.('success', 'Hareket kaydı silindi.');
    } catch (e) {
      onNotify?.('error', e.message);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Gelen / Giden</h1>
        <p>{AYLAR[ay]} {yil} — Ürün bazlı stok hareketleri</p>
      </div>

      <div className="kat-tabs">
        {KATEGORILER.map(k => (
          <button
            key={k.key}
            className={`kat-tab ${kategori === k.key ? 'active' : ''}`}
            onClick={() => {
              setKategori(k.key);
              setSeciliUrunId(null);
            }}
          >
            <span><k.Icon size={16} strokeWidth={2.2} /></span>
            {k.label}
          </button>
        ))}
      </div>

      <div className="movement-toolbar">
        {ayKapaliHam && !donemAcik && (
          <button onClick={onKilitAc} className="period-lock-btn">
            Bu dönem kapalıdır — Açmak için tıkla
          </button>
        )}
        {ayKapaliHam && donemAcik && (
          <span className="period-open-badge">Dönem Açık</span>
        )}
        {!ayKapali && (
          <div className="movement-action-group">
            <button className="btn btn-success" onClick={() => modalAc(seciliUrun, 'giris')}>
              <ChevronUp size={17} /> Giriş Ekle
            </button>
            <button className="btn btn-danger" onClick={() => modalAc(seciliUrun, 'cikis')}>
              <ChevronDown size={17} /> Çıkış Ekle
            </button>
          </div>
        )}
      </div>

      {!seciliUrun ? (
        <div className="card">
          <div className="product-list-head">
            <div>
              <div className="product-list-title">
                <span><KategoriIcon size={20} strokeWidth={2.2} /></span>
                {kategoriAktif.label} Ürünleri
              </div>
              <div className="product-list-subtitle">Detay görmek için bir ürüne tıklayın.</div>
            </div>
            <div className="product-list-count">{filtreliUrunler.length} ürün</div>
          </div>

          <div className="movement-search-row">
            <div className="barcode-search-input">
              <ScanBarcode size={18} />
              <input
                placeholder="Ürün adı, ID veya barkod..."
                value={arama}
                onChange={e => {
                  setArama(e.target.value);
                  setSeciliUrunId(null);
                }}
              />
              <button type="button" onClick={kameraBaslat} disabled={kameraAcik} title="Kamera ile barkod okut">
                <Camera size={18} />
              </button>
            </div>
          </div>

          {kameraAcik && (
            <div className="inline-barcode-camera">
              <video ref={videoRef} muted playsInline />
              <div className="barcode-camera-line" />
              <button type="button" onClick={kamerayiKapat} title="Kamerayı kapat">
                <X size={18} />
              </button>
            </div>
          )}

          {yukleniyor ? (
            <div className="empty-state">Yükleniyor...</div>
          ) : filtreliUrunler.length === 0 ? (
            <div className="empty-state">Bu kategori için ürün bulunamadı.</div>
          ) : (
            <div className="product-card-grid">
              {filtreliUrunler.map(u => {
                const durum = u.guncel_stok <= 0 ? 'empty' : u.guncel_stok < 10 ? 'low' : 'ok';
                return (
                  <button key={u.id} className={`product-stock-card ${durum}`} onClick={() => setSeciliUrunId(u.id)}>
                    <div className="product-stock-top">
                      <div>
                        <div className="product-name">{u.ad}</div>
                        <div className="product-code">{u.urun_id}</div>
                      </div>
                      <span className="badge badge-cat">{kategoriBilgi(u.kategori).label}</span>
                    </div>
                    <div className="product-stock-main">
                      <div>
                        <span className="metric-label">Güncel</span>
                        <strong>{sayi(u.guncel_stok)}</strong>
                      </div>
                      <div>
                        <span className="metric-label">Gelen</span>
                        <strong className="metric-green">+{sayi(u.gelen)}</strong>
                      </div>
                      <div>
                        <span className="metric-label">Çıkış</span>
                        <strong className="metric-red">-{sayi(u.giden)}</strong>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="product-detail-shell">
          <div className="detail-header">
            <button className="btn btn-secondary btn-sm" onClick={() => setSeciliUrunId(null)}>← Ürünler</button>
            <div className="detail-title">
              <span>{(() => {
                const Icon = kategoriBilgi(seciliUrun.kategori).Icon;
                return <Icon size={20} strokeWidth={2.2} />;
              })()}</span>
              <strong>{seciliUrun.ad}</strong>
              <span>— {AYLAR[ay]} {yil}</span>
            </div>
          </div>

          <div className="detail-meta-row">
            <span className="detail-label">Ürün ID</span>
            <strong>{seciliUrun.urun_id}</strong>
            <span className="detail-label">Kategori</span>
            <strong>{kategoriBilgi(seciliUrun.kategori).label}</strong>
            <span className="detail-label">Birim Fiyat</span>
            <strong>₺{Number(seciliUrun.fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
          </div>

          <div className="detail-stat-grid">
            <div className="detail-stat amber">
              <div className="detail-stat-icon"><Boxes size={27} /></div>
              <div>
                <span>Devreden (Eski Stok)</span>
                <strong>{sayi(seciliUrun.devreden_stok)}</strong>
                <small>adet</small>
              </div>
            </div>
            <div className="detail-stat green">
              <div className="detail-stat-icon"><PackageCheck size={27} /></div>
              <div>
                <span>Gelen Ürün</span>
                <strong>{sayi(seciliUrun.gelen)}</strong>
                <small>adet</small>
              </div>
            </div>
            <div className="detail-stat red">
              <div className="detail-stat-icon"><PackageOpen size={27} /></div>
              <div>
                <span>Çıkış</span>
                <strong>{sayi(seciliUrun.giden)}</strong>
                <small>adet</small>
              </div>
            </div>
            <div className="detail-stat blue">
              <div className="detail-stat-icon"><Package size={27} /></div>
              <div>
                <span>Güncel Stok</span>
                <strong>{sayi(seciliUrun.guncel_stok)}</strong>
                <small>adet</small>
              </div>
            </div>
          </div>

          <div className="detail-value-grid">
            <div className="detail-value-card">
              <span>Devreden Mal Değeri</span>
              <strong>{para(devredenDegeri)}</strong>
            </div>
            <div className="detail-value-card incoming">
              <span>Gelen Mal Değeri</span>
              <strong>{para(gelenDegeri)}</strong>
            </div>
            <div className="detail-value-card outgoing">
              <span>Kullanılan Mal Değeri</span>
              <strong>{para(kullanilanDeger)}</strong>
            </div>
            <div className="detail-value-card current">
              <span>Güncel Stok Değeri</span>
              <strong>{para(guncelDeger)}</strong>
            </div>
            <div className="detail-value-card usage">
              <div className="detail-value-usage-head">
                <span>Kullanım %</span>
                <strong style={{ color: urunKullanimRengi }}>
                  {urunKullanimYuzdesi === null ? '—' : `%${urunKullanimYuzdesi}`}
                </strong>
              </div>
              <div className="product-usage-track">
                <div style={{ width: `${Math.min(urunKullanimYuzdesi ?? 0, 100)}%`, background: urunKullanimRengi }} />
              </div>
            </div>
          </div>

          <div className="detail-table-card">
            <div className="detail-table-title"><CalendarDays size={17} /> Günlük Hareket Listesi <span>{gunlukHareketler.length} gün · {seciliHareketler.length} kayıt</span></div>
            <div className="table-wrap movement-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th style={{ textAlign: 'center' }}>Giriş</th>
                    <th style={{ textAlign: 'right' }}>Giriş Tutarı</th>
                    <th style={{ textAlign: 'center' }}>Çıkış</th>
                    <th style={{ textAlign: 'right' }}>Çıkış Tutarı</th>
                    <th style={{ textAlign: 'right' }}>Fiyat</th>
                    <th style={{ textAlign: 'right' }}>Tutar</th>
                    <th>Açıklama</th>
                    <th style={{ width: 54 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {gunlukHareketler.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: 28 }}>
                        Bu ürün için seçili ayda hareket yok.
                      </td>
                    </tr>
                  )}
                  {gunlukHareketler.map(g => {
                    const acik = acikGun === g.key;
                    return (
                      <Fragment key={g.key}>
                        <tr className="daily-summary-row" onClick={() => setAcikGun(acik ? null : g.key)}>
                          <td style={{ fontWeight: 800 }}>
                            <span className="daily-toggle">{acik ? '−' : '+'}</span>
                            {tarihFmt(g.tarih_iso || g.tarih)}
                          </td>
                          <td style={{ textAlign: 'center', color: '#059669', fontWeight: 800 }}>
                            {g.giris > 0 ? sayi(g.giris) : '-'}
                          </td>
                          <td style={{ textAlign: 'right', color: '#059669', fontWeight: 800 }}>
                            {g.girisDeger > 0 ? fiyatFmt(g.girisDeger) : '-'}
                          </td>
                          <td style={{ textAlign: 'center', color: '#dc2626', fontWeight: 800 }}>
                            {g.cikis > 0 ? sayi(g.cikis) : '-'}
                          </td>
                          <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 800 }}>
                            {g.cikisDeger > 0 ? fiyatFmt(g.cikisDeger) : '-'}
                          </td>
                          <td></td>
                          <td></td>
                          <td style={{ color: '#8a928c', fontSize: 12 }}>{g.kayitlar.length} kayıt</td>
                          <td></td>
                        </tr>
                        {acik && g.kayitlar.map(h => (
                          <tr key={h.id} className="daily-detail-row">
                            <td className="movement-time-cell"><span>{saatFmt(h) || 'Saat yok'}</span></td>
                            <td style={{ textAlign: 'center', color: '#059669', fontWeight: 700 }}>
                              {h.hareket_turu === 'giris' ? sayi(h.miktar) : '-'}
                            </td>
                            <td style={{ textAlign: 'right', color: '#059669', fontWeight: 700 }}>
                              {h.hareket_turu === 'giris' ? fiyatFmt(h.hareket_degeri) : '-'}
                            </td>
                            <td style={{ textAlign: 'center', color: '#dc2626', fontWeight: 700 }}>
                              {h.hareket_turu === 'cikis' ? sayi(h.miktar) : '-'}
                            </td>
                            <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>
                              {h.hareket_turu === 'cikis' ? fiyatFmt(h.hareket_degeri) : '-'}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fiyatFmt(h.birim_fiyat)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fiyatFmt(h.hareket_degeri)}</td>
                            <td style={{ color: '#8a928c', fontSize: 12 }}>{aciklamaFmt(h)}</td>
                            <td style={{ textAlign: 'right' }}>
                              {!ayKapali && (
                                <div className="row-action-wrap">
                                  <button className="row-action-btn" onClick={() => setAcikMenu(acikMenu === h.id ? null : h.id)} title="İşlemler"><MoreVertical size={17} /></button>
                                  {acikMenu === h.id && (
                                    <div className="row-action-menu">
                                      <button onClick={() => duzenleModalAc(h)}>Düzenle</button>
                                      <button
                                        className="danger"
                                        onClick={() => onConfirm?.({
                                          title: 'Hareket kaydı silinsin mi?',
                                          message: `${tarihFmt(h.tarih)} tarihli ${h.hareket_turu === 'giris' ? 'giriş' : 'çıkış'} kaydı silinecek.`,
                                          detail: 'Bu işlem stok hesaplarını etkiler ve geri alınamaz.',
                                          confirmText: 'Evet, Sil',
                                          onConfirm: () => silHareket(h.id),
                                        })}
                                      >
                                        Sil
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mobile-movement-list">
              {gunlukHareketler.length === 0 && (
                <div className="empty-state">Bu ürün için seçili ayda hareket yok.</div>
              )}
              {gunlukHareketler.map(g => {
                const acik = acikGun === g.key;
                const tip = g.cikis > 0 && g.giris === 0 ? 'cikis' : g.giris > 0 && g.cikis === 0 ? 'giris' : 'karma';
                return (
                  <div key={g.key} className={`mobile-movement-item ${tip}`}>
                    <button type="button" className="mobile-movement-summary" onClick={() => setAcikGun(acik ? null : g.key)}>
                      <div>
                        <div className="mobile-movement-date">{tarihFmt(g.tarih_iso || g.tarih)}</div>
                        <div className="mobile-movement-note">{g.kayitlar.length} kayıt</div>
                      </div>
                      <div className="mobile-movement-day-totals">
                        {g.giris > 0 && <span className="amount-in"><em>Giriş</em><strong>+{sayi(g.giris)}</strong><small>{fiyatFmt(g.girisDeger)}</small></span>}
                        {g.cikis > 0 && <span className="amount-out"><em>Çıkış</em><strong>-{sayi(g.cikis)}</strong><small>{fiyatFmt(g.cikisDeger)}</small></span>}
                      </div>
                    </button>
                    {acik && (
                      <div className="mobile-movement-details">
                        {g.kayitlar.map(h => (
                          <div key={h.id} className="mobile-movement-detail-row">
                            <div>
                              <div className="mobile-movement-date">Saat {saatFmt(h) || 'yok'}</div>
                              <div className="mobile-movement-note">
                                {aciklamaFmt(h)} · Fiyat: {fiyatFmt(h.birim_fiyat)} · Tutar: {fiyatFmt(h.hareket_degeri)}
                              </div>
                            </div>
                            <div className="mobile-movement-amount">
                              <span>{h.hareket_turu === 'giris' ? 'Giriş' : 'Çıkış'}</span>
                              <strong className={h.hareket_turu === 'giris' ? 'amount-in' : 'amount-out'}>{h.hareket_turu === 'giris' ? '+' : '-'}{sayi(h.miktar)}</strong>
                            </div>
                            {!ayKapali && (
                              <div className="mobile-movement-actions">
                                <button onClick={() => duzenleModalAc(h)}>Düzenle</button>
                                <button
                                  className="danger"
                                  onClick={() => onConfirm?.({
                                    title: 'Hareket kaydı silinsin mi?',
                                    message: `${tarihFmt(h.tarih)} tarihli ${h.hareket_turu === 'giris' ? 'giriş' : 'çıkış'} kaydı silinecek.`,
                                    detail: 'Bu işlem stok hesaplarını etkiler ve geri alınamaz.',
                                    confirmText: 'Evet, Sil',
                                    onConfirm: () => silHareket(h.id),
                                  })}
                                >
                                  Sil
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {harModal && (
        <div className="modal-overlay" onClick={() => setHarModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className={`modal-title movement-modal-title ${form.hareket_turu === 'giris' ? 'giris' : 'cikis'}`}>
                {form.hareket_turu === 'giris' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                {form.hareket_turu === 'giris'
                  ? `Giriş ${duzenlenenHareket ? 'Düzenle' : 'Ekle'}`
                  : `Çıkış ${duzenlenenHareket ? 'Düzenle' : 'Ekle'}`}
              </div>
              <button className="modal-close-btn" type="button" onClick={() => { setHarModal(false); setDuzenlenenHareket(null); }}>×</button>
            </div>

            <form onSubmit={kaydet}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label>Tarih</label>
                <input type="date" value={form.tarih} onChange={e => setForm(f => ({ ...f, tarih: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label>Ürün</label>
                <select value={form.urun_id} onChange={e => {
                  const urun = urunler.find(u => String(u.id) === e.target.value);
                  setForm(f => ({
                    ...f,
                    urun_id: e.target.value,
                    birim_fiyat: f.hareket_turu === 'giris' && urun ? String(urun.fiyat ?? '') : f.birim_fiyat
                  }));
                }}>
                  <option value="">Ürün Seçin</option>
                  {urunler.map(u => <option key={u.id} value={String(u.id)}>{u.ad} — Stok: {sayi(u.guncel_stok)} — Son fiyat: {fiyatFmt(u.fiyat)}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label>Miktar</label>
                <input type="number" step="0.01" min="0.01" value={form.miktar}
                  onChange={e => setForm(f => ({ ...f, miktar: e.target.value }))}
                  placeholder="0" inputMode="decimal" />
              </div>
              {form.hareket_turu === 'giris' && (
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Fiyat</label>
                  <input type="number" step="0.01" min="0" value={form.birim_fiyat}
                    onChange={e => setForm(f => ({ ...f, birim_fiyat: e.target.value }))}
                    placeholder="0.00" inputMode="decimal" />
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Açıklama (opsiyonel)</label>
                <input value={form.aciklama} onChange={e => setForm(f => ({ ...f, aciklama: e.target.value }))} placeholder="Not ekle..." />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setHarModal(false); setDuzenlenenHareket(null); }}>İptal</button>
                <button type="submit" className={`btn ${form.hareket_turu === 'giris' ? 'btn-success' : 'btn-danger'}`} style={{ flex: 2 }}>
                  {form.hareket_turu === 'giris' ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                  {form.hareket_turu === 'giris'
                    ? `Giriş ${duzenlenenHareket ? 'Güncelle' : 'Kaydet'}`
                    : `Çıkış ${duzenlenenHareket ? 'Güncelle' : 'Kaydet'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
